import { Button } from '@pickleballinc/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import clsx from 'clsx';

interface ActionButtonsProps {
	closeButtonText: string;
	onClose: () => void;
	submitButtonText: string;
	className?: string;
	closeButtonClassName?: string;
	submitButtonClassName?: string;
	onSubmit?: () => void;
	submitButtonDisabled?: boolean;
	icon?: (disabled: boolean) => React.ReactNode;
	iconClassName?: string;
	isLoading?: boolean;
	submitTooltipMessage?: string;
}

export const ActionButtons = ({
	className,
	closeButtonText,
	closeButtonClassName,
	onClose,
	submitButtonText,
	submitButtonClassName,
	onSubmit,
	submitButtonDisabled = false,
	icon,
	iconClassName,
	isLoading = false,
	submitTooltipMessage
}: ActionButtonsProps) => {
	const submitButton = (
		<Button
			type="submit"
			onClick={onSubmit}
			disabled={submitButtonDisabled || isLoading}
			className={clsx(
				`w-[7.5rem] text-sm font-semibold leading-5`,
				{
					'!cursor-not-allowed border !border-gray-300 !bg-gray-100 !text-gray-400 disabled:pointer-events-auto':
						submitButtonDisabled && !isLoading
				},
				submitButtonClassName
			)}
		>
			{isLoading ? (
				<div className="flex items-center gap-2">
					<span className="inline-block size-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
					<span>Saving...</span>
				</div>
			) : (
				<>
					{!!icon && <span className={`${iconClassName}`}>{icon(submitButtonDisabled)}</span>}
					{submitButtonText}
				</>
			)}
		</Button>
	);

	return (
		<div className={`flex gap-4 ${className}`}>
			<Button
				type="button"
				onClick={onClose}
				className={`w-[7.5rem] border border-solid border-gray-300 bg-white text-sm font-semibold leading-5 !text-black hover:bg-gray-100 ${closeButtonClassName}`}
				variant="secondary"
			>
				{closeButtonText}
			</Button>
			{submitButtonDisabled && submitTooltipMessage ? (
				<TooltipProvider delayDuration={0} skipDelayDuration={0}>
					<Tooltip>
						<TooltipTrigger asChild>{submitButton}</TooltipTrigger>
						<TooltipPortal>
							<TooltipContent className="bg-gray-900">
								<p>{submitTooltipMessage}</p>
							</TooltipContent>
						</TooltipPortal>
					</Tooltip>
				</TooltipProvider>
			) : (
				submitButton
			)}
		</div>
	);
};
